import React, { useEffect, useRef, useState } from 'react';
import { Skeleton } from '@mui/material';
import { calculateImageColor } from '@Saral-Influencer-Marketing/saral-utils/src/functions/image';
import { Navigation, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import './image-viewer.scss';

import noPreviewImage from '../../../../../../images/NoPreview.png';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../../../Icons';
import CardStats from '../CardStats';
import CardTopContent from '../CardTopContent';

const getContentImages = (content) => {
  if (!content) return [];

  switch (content.type) {
    case 'story':
      // If content.image exists and is an array, use it directly
      if (Array.isArray(content.image)) {
        return content.image;
      }
      // If content.images exists with url
      if (content.images?.[0]?.url) {
        return Array.isArray(content.images[0].url) ? content.images[0].url : [content.images[0].url];
      }
      // Try media as fallback
      if (content.media?.[0]?.url) {
        return [content.media[0].url];
      }
      return [];
    case 'post':
      // Handle array of image objects with url property
      if (content.images?.length > 0) {
        return content.images.map((img) => img.url).filter(Boolean);
      }
      // Fallback to media if images not present
      if (content.media?.length > 0) {
        return content.media.map((m) => m.url || m).filter(Boolean);
      }
      // Final fallback to single url or image
      return content.url ? [content.url] : content.image ? [content.image] : [];
    default:
      if (Array.isArray(content.image)) {
        return content.image;
      }
      return content.image ? [content.image] : [];
  }
};

const ImageViewer = ({ content }) => {
  const images = getContentImages(content);

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loadedImages, setLoadedImages] = useState({});
  const imgs = useRef([]);
  const showLeftArrow = currentSlide !== 0;
  const showRightArrow = currentSlide !== images.length - 1;

  const [refreshKey, setRefreshKey] = useState(0);
  const [slideColors, setSlideColors] = useState({});

  useEffect(() => {
    // Refresh URLs every 50 minutes (before 1 hour expiry)
    const timer = setInterval(
      () => {
        setRefreshKey((prev) => prev + 1);
      },
      50 * 60 * 1000
    );

    return () => clearInterval(timer);
  }, []);

  const handleImageLoad = async (imageElement, index) => {
    setLoadedImages((prev) => ({
      ...prev,
      [index]: true,
    }));

    // Only calculate color for non-story content
    if (content?.type?.toLowerCase() !== 'story') {
      const color = await calculateImageColor(imageElement);
      if (color) {
        setSlideColors((prev) => ({
          ...prev,
          [index]: color,
        }));
      }
    }
  };

  const openImageInNewTab = () => {
    if (content?.entityUrl) {
      window.open(content?.entityUrl, '_blank');
    }
  };

  return (
    <div className={`image-viewer-container ${content?.type}`}>
      <div className="top-container">
        <CardTopContent
          profile={content?.profile}
          downloadUrl={content.images?.[currentSlide]?.url}
          entityUrl={content?.entityUrl}
        />
      </div>
      <div className="gradient-overlay-top" onClick={() => openImageInNewTab()} />
      <div className="gradient-overlay-bottom" onClick={() => openImageInNewTab()} />

      {/* Pagination dots */}
      {images?.length > 1 && (
        <div className="pagination-dots">
          {images.map((_, index) => (
            <div key={index} className={`pagination-dot ${index === currentSlide ? 'active' : ''}`} />
          ))}
        </div>
      )}

      {images?.length > 1 && (
        <>
          <div
            className={`left-carousel-arrow-container left-swiper-arrow-${content?._id} toggle-arrows ${
              !showLeftArrow ? 'hidden' : ''
            }`}>
            <ChevronLeftIcon width={20} height={20} color="var(--neutral-n-100)" />
          </div>
          <div
            className={`right-carousel-arrow-container right-swiper-arrow-${content?._id} toggle-arrows ${
              !showRightArrow ? 'hidden' : ''
            }`}>
            <ChevronRightIcon width={20} height={20} color="var(--neutral-n-100)" />
          </div>
        </>
      )}

      <Swiper
        key={refreshKey}
        navigation={{
          nextEl: `.right-swiper-arrow-${content?._id}`,
          prevEl: `.left-swiper-arrow-${content?._id}`,
        }}
        modules={[Pagination, Navigation]}
        className="mySwiper"
        onSlideChange={(s) => {
          setCurrentSlide(s.activeIndex);
        }}>
        {images?.map((image, index) => (
          <SwiperSlide
            key={index}
            virtualIndex={index}
            style={{
              backgroundColor: slideColors[index] || 'transparent',
              transition: 'background-color 0.3s ease',
            }}>
            {!loadedImages[index] && (
              <div className="image-shimmer-container">
                <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
              </div>
            )}
            <img
              src={image || noPreviewImage}
              className={`content-image ${loadedImages[index] ? 'loaded' : 'loading'} ${content?.entityUrl ? 'cursor-pointer' : ''}`}
              ref={(ref) => {
                imgs.current[index] = ref;
              }}
              alt="No Preview"
              onLoad={(e) => handleImageLoad(e.target, index)}
              onClick={() => openImageInNewTab()}
              loading="lazy"
              crossOrigin="anonymous"
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <div className="bottom-container">
        <CardStats
          comments={content?.comments}
          likes={content?.likes}
          saves={content?.saves}
          shares={content?.shares}
          views={content?.views}
          type={content?.type}
          post={content}
        />
      </div>
    </div>
  );
};

export default ImageViewer;
